import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageShell from './PageShell';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import Pagination from './components/Pagination';
import { useMetrcFacilityOptions } from './hooks/useMetrcImportOptions';
import { useClientPagination } from './hooks/useClientPagination';
import type { MetrcFacilityDocument } from './types/index';

interface MetrcImportDialogProps {
  organizationId?: number;
  marketId: number;
  market: string;
  username?: string;
  onClose: () => void;
  onSave: (data: { marketId: number; username: string; facilities: MetrcFacilityDocument[] }) => void;
  /** When true, only facilities already imported into RID are selectable (no global Metrc facility list). */
  restrictToImportedFacilities?: boolean;
  /** When true, only facilities that can still be imported are listed (already-imported facilities are left out). */
  excludeImportedFacilities?: boolean;
}

export default function MetrcImportDialog(
  { organizationId, marketId, market, username = '', onClose, onSave, restrictToImportedFacilities = false, excludeImportedFacilities = false }
  : MetrcImportDialogProps) {
  const { facilities, loading, loadError } = useMetrcFacilityOptions(
    organizationId, marketId, restrictToImportedFacilities, excludeImportedFacilities);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set<string>());
  const [search, setSearch] = useState('');
  const toggleAllRef = useRef<HTMLInputElement | null>(null);
  const pageSize = 10;
  const hasFacilities = facilities.length > 0;
  const canRenderList = !loading && !loadError;

  const filteredFacilities = useMemo(() => {
    if (!search.trim()) return facilities;
    const term = search.trim().toLowerCase();
    return facilities.filter(f =>
      (f.name || '').toLowerCase().includes(term) ||
      (f.license || '').toLowerCase().includes(term)
    );
  }, [facilities, search]);

  const showSelectAll = canRenderList && filteredFacilities.length > 0;

  useEffect(() => {
    setSelected(new Set<string>());
    resetPage();
    setErrors({});
  }, [organizationId, marketId, restrictToImportedFacilities, excludeImportedFacilities]);

  const {
    page,
    totalPages,
    pagedItems: pagedFacilities,
    resetPage,
    previousPage,
    nextPage,
  } = useClientPagination(filteredFacilities, pageSize);

  const allSelected = hasFacilities && filteredFacilities.length > 0 && filteredFacilities.every(f => selected.has(f.license));
  const noneSelected = !filteredFacilities.some(f => selected.has(f.license));
  const someSelected = !noneSelected && !allSelected;

  useEffect(() => {
    if (!toggleAllRef.current) return;
    toggleAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleFacility = (facilityLicense) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(facilityLicense)) next.delete(facilityLicense);
      else next.add(facilityLicense);
      return next;
    });
  };

  const setAll = (checked: boolean) => {
    const filteredLicenses = filteredFacilities.map((f) => String(f.license));
    setSelected(prev => {
      const next = new Set(prev);
      filteredLicenses.forEach(lic => {
        if (checked) next.add(lic);
        else next.delete(lic);
      });
      return next;
    });
  };

  const handleSave = () => {
    const nextErrors: Record<string, any> = {};
    if (selected.size === 0) nextErrors.selection = 'Select at least one facility';
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    if (Object.keys(nextErrors).length > 0) return;
    onSave({
      marketId,
      username,
      facilities: facilities
        .filter((f) => selected.has(f.license))
        .map((f) => ({ ...f }))
    });
  };

  const footerButtons = (
    <DialogFooter>
      <button className="button" onClick={handleSave} data-testid="metrc-facilities-next">Import Selected Facilities</button>
    </DialogFooter>
  );

  return (
    <PageShell
      contextLabel="Integrations"
      title="Connect Facilities"
      onClose={onClose}
      framed={false}
      testId="metrc-facilities"
      className="metrc-import-shell"
      preview={(
        <div className="card" style={{ padding: 16 }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Facility Connections</div>
          <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6, marginBottom: 10 }}>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Market:</span> {market || '—'}</div>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Username:</span> {username || '—'}</div>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Selected:</span> {selected.size} facilit{selected.size === 1 ? 'y' : 'ies'}</div>
          </div>
          <div className="divider" style={{ margin: '12px 0' }} />
          <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Selected Facilities</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {selected.size === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>None selected yet.</div>
            ) : (
              Array.from(selected).slice(0, 6).map((license) => {
                const lic = String(license);
                const f = facilities.find((x) => String(x.license) === lic);
                return (
                  <div key={lic} className="card" style={{ padding: 10 }}>
                    <div style={{ fontWeight: 800, fontSize: 13 }}>{f?.name || lic}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{f?.license || lic}</div>
                  </div>
                );
              })
            )}
            {selected.size > 6 && (
              <div className="muted" style={{ fontSize: 12 }}>+{selected.size - 6} more</div>
            )}
          </div>
        </div>
      )}
      alert={<DialogAlert show={Object.keys(errors).length > 0} />}
      footer={footerButtons}
    >
      <div className="divider" style={{ marginBottom: 10 }} />

      <div className="form-grid">

        <div className="field full-span">
          <label className="label">Market</label>
          <input className="input" value={market || '—'} readOnly disabled data-testid="metrc-facilities-market" />
        </div>

        <div className="field full-span">

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <label className="label" style={{ marginBottom: 0 }}>Facilities</label>
              <div className="muted" style={{ fontSize: 12 }}>Select the facilities to pull products from</div>
            </div>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 8, minHeight: 300, overflowY: 'auto' }}>
            <div style={{ padding: '4px 4px 8px' }}>
              <input
                className="input"
                type="text"
                placeholder="Search by name or license..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                data-testid="metrc-facilities-search"
                style={{ width: '100%' }}
              />
            </div>
            {loading && (
              <div className="muted" style={{ padding: 8 }}>Loading facilities...</div>
            )}
            {!loading && loadError && (
              <div className="error-text" style={{ padding: 8 }}>{loadError}</div>
            )}
            {canRenderList && !loadError && !hasFacilities && (
              <div className="muted" style={{ padding: 8 }}>No facilities returned for this market.</div>
            )}
            {canRenderList && !loadError && hasFacilities && filteredFacilities.length === 0 && (
              <div className="muted" style={{ padding: 8 }}>No facilities match your search.</div>
            )}
            {showSelectAll && (
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: 10, padding: '6px 4px', borderBottom: '1px solid var(--border)' }}>
                <input
                  ref={toggleAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => setAll(e.target.checked)}
                  aria-label="Select all facilities"
                  data-testid="metrc-facilities-check-all"
                />
                <div className="muted" style={{ fontSize: 12, fontWeight: 700 }}>Select all</div>
              </div>
            )}
            {canRenderList && !loadError && pagedFacilities.map((f) => (
              <div key={f.license} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: 10, padding: '6px 4px' }}>
                <input
                  id={`facility-${f.license}`}
                  type="checkbox"
                  checked={selected.has(f.license)}
                  onChange={() => toggleFacility(f.license)}
                  data-testid={`metrc-facilities-check-${String(f.license || '').toLowerCase()}`}
                />
                <label htmlFor={`facility-${f.license}`} style={{ display: 'block', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 700 }}>{f.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{f.license}</div>
                </label>
              </div>
            ))}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={previousPage}
            onNext={nextPage}
            style={{ marginTop: 10 }}
            format="of"
            prevLabel="◀"
            nextLabel="▶"
          />

          {errors.selection && <div className="error-text">{errors.selection}</div>}
        </div>

      </div>
      <div className="divider" style={{ marginTop: 12 }} />
    </PageShell>
  );
}
